/**
 * Human Call Script lifecycle, extracted from DefaultCaseDetailsPage.
 *
 * The script has no tab of its own (BV-1575): it is a column of the Review view, shown
 * for the case's latest human call. These hooks own the phase gate and the edit gates
 * that column runs on.
 *
 * Split in two by data-availability, mirroring where the page can compute each:
 * - `useHumanCallVisibility` — resolves `callScriptAvailable` EARLY (human-call state + an
 *   active Call Script) because the page's initial-tab effect reads it: when a script is
 *   on offer the page lands on Review, where the script lives. NOT gated on assignment —
 *   anyone who can see the case can read the script. Assignment still gates editing and
 *   the follow-the-live-call effect, both resolved in the second hook from its own
 *   `isAssignee`. Per-workflow enablement is data-driven via the active-script check (no
 *   env-var allowlist). Generally NOT gated on a live call either, so the script covers
 *   the whole human-call phase — except for the workflows in
 *   `CALL_SCRIPT_LIVE_CALL_ONLY_WORKFLOW_TYPES`, which keep the older live-call-only
 *   behaviour and need `liveCallId` passed in.
 * - `useHumanCallEditState` — editability/active gates plus the clear-heard and
 *   follow-the-live-call effects, computed LATE because they need `callDetails` /
 *   `hasSavedCallDisposition`, which only resolve after the selected-call query.
 *
 * Both are called at their original page positions so effect order and timing are unchanged.
 */
import type { CallInfo } from '@compass/client/types/call';
import { CallStatus, CallType } from '@compass/client/types/call';
import type { GetCaseRecordResponse } from '@compass/client/types/case_record';
import { useEffect, useMemo, useRef } from 'react';
import { toast } from 'sonner';

import type { CaseDetailsTabType } from '@/constants/caseDetails';
import { callScriptRequiresLiveCall, CaseDetailsTabs } from '@/constants/caseDetails';
import { useActiveScript } from '@/hooks/useActiveScript';
import { isHumanCallState } from '@/utils/caseStateHelpers';

import { clearHeard } from './heard';

export interface UseHumanCallVisibilityArgs {
  caseDetails: GetCaseRecordResponse | undefined;
  /**
   * The case's currently-live call id (store-driven, set once Twilio's callback creates
   * the call object). Only consulted for workflows in
   * {@link CALL_SCRIPT_LIVE_CALL_ONLY_WORKFLOW_TYPES}, which show the script solely for
   * the duration of a call; ignored for every other workflow.
   */
  liveCallId?: string;
}

export interface HumanCallVisibilityState {
  /** The case is in a human-call phase AND its workflow has an active Call Script. */
  callScriptAvailable: boolean;
}

/**
 * Pre-script gate: is this case in a phase where the Call Script should be offered at all?
 *
 * Deliberately NOT gated on assignment — the script is reference material for anyone who
 * can already see the case (leads shadowing a call, the next auditor), while assignment
 * still governs who can EDIT and whose view follows the live call.
 *
 * Most workflows are not gated on a live call either, so the script covers the whole
 * human-call phase. The workflows in `CALL_SCRIPT_LIVE_CALL_ONLY_WORKFLOW_TYPES` keep the
 * older behaviour: the script appears only once the call object exists (Twilio callback →
 * `liveCallId`) and goes away when the call ends.
 */
function caseWantsCallScript(
  caseDetails: GetCaseRecordResponse | undefined,
  liveCallId: string | undefined,
): boolean {
  if (!isHumanCallState(caseDetails?.state)) return false;
  if (!callScriptRequiresLiveCall(caseDetails?.workflow_type)) return true;
  return !!liveCallId;
}

export function useHumanCallVisibility(args: UseHumanCallVisibilityArgs): HumanCallVisibilityState {
  const { caseDetails, liveCallId } = args;

  // Per-workflow enablement is data-driven: the active-script check below rules out any
  // workflow with no linked Call Script, so no env-var allowlist is needed. Only when this
  // gate passes do we resolve the active script.
  const wantsCallScript = caseWantsCallScript(caseDetails, liveCallId);

  // A script is on offer only when the gate passes AND the workflow has an active Call
  // Script. Declared here (above the initial-tab effect, which reads it to land on
  // Review); editability is computed later from callDetails.
  const { data: activeScriptData } = useActiveScript(
    wantsCallScript ? caseDetails?.workflow_id : undefined,
  );
  const callScriptAvailable = wantsCallScript && Boolean(activeScriptData?.script);

  return { callScriptAvailable };
}

/**
 * A live human call ended: drop the assignee's local heard cache (keys only, no PHI — the
 * backend is the durable source) and move them to the recording (Review + latest call),
 * which is also where the script column lives. Extracted from the effect to keep it simple.
 */
function moveToCallRecording(args: {
  lastHumanCallId: string | undefined;
  latestCallId: string | undefined;
  setActiveTab: (t: CaseDetailsTabType) => void;
  setSelectedCallId: (id: string) => void;
  setManuallySelectedCallId: (id: string) => void;
}): void {
  const {
    lastHumanCallId,
    latestCallId,
    setActiveTab,
    setSelectedCallId,
    setManuallySelectedCallId,
  } = args;
  if (lastHumanCallId) clearHeard(lastHumanCallId);
  if (latestCallId) {
    setSelectedCallId(latestCallId);
    setManuallySelectedCallId(latestCallId);
  }
  setActiveTab(CaseDetailsTabs.REVIEW);
  toast.info('The call has ended — please review the call.');
}

export interface UseHumanCallEditStateArgs {
  callScriptAvailable: boolean;
  /**
   * The LIVE call, or undefined when none is up. Drives the follow-the-live-call effects
   * only (select-on-dial, moveToCallRecording on hang-up) — NOT editability, which would
   * close the moment the call ended.
   */
  callDetails: CallInfo | null | undefined;
  /**
   * The call currently SELECTED in the attempts strip — what the script actually reads and
   * writes. Editability keys off this so the post-hang-up write-up window stays open: the
   * call still exists and is still selected, it is simply no longer live.
   */
  selectedCallDetails: CallInfo | null | undefined;
  /**
   * Whether the SELECTED call already carries a disposition. No longer gates editing (the
   * script becomes the Output board post-call, which has no such gate) — it only drives the
   * heard-marks cleanup below.
   */
  hasSavedCallDisposition: boolean;
  caseDetails: GetCaseRecordResponse | undefined;
  isCurrentUserAssigned: (
    assignedEmail: string | null | undefined,
    assignedUserId?: string | null | undefined,
  ) => boolean;
  setActiveTab: (t: CaseDetailsTabType) => void;
  setSelectedCallId: (id: string) => void;
  setManuallySelectedCallId: (id: string) => void;
  latestCallId: string | undefined;
}

export interface HumanCallEditState {
  humanCallEditable: boolean;
  humanCallActive: boolean;
}

export function useHumanCallEditState(args: UseHumanCallEditStateArgs): HumanCallEditState {
  const {
    callScriptAvailable,
    callDetails,
    selectedCallDetails,
    hasSavedCallDisposition,
    caseDetails,
    isCurrentUserAssigned,
    setActiveTab,
    setSelectedCallId,
    setManuallySelectedCallId,
    latestCallId,
  } = args;

  // Assignment no longer gates VISIBILITY (the script is readable by every viewer), so
  // it is resolved here on its own: it still gates editing and the auto-focus effects.
  const isAssignee = useMemo(
    () => isCurrentUserAssigned(caseDetails?.assigned, caseDetails?.assigned_user_id),
    [caseDetails?.assigned, caseDetails?.assigned_user_id, isCurrentUserAssigned],
  );

  // Can FILL/UPDATE output values: requires a script on offer, a human CALL THAT EXISTS,
  // the assigned user, and no saved disposition yet.
  //
  // Keyed on the SELECTED call, not the live one. The call-id requirement is what makes the
  // pre-call "Human Call Preview" read-only: with no call there is nothing to capture INTO,
  // so the script previews the case's output rather than pretending to accept edits. Editing
  // opens the moment the call is created on dial and stays open through the write-up.
  //
  // Deliberately NOT gated on a saved disposition. Post-PCE the call script gains the
  // Output board's affordances, and that board has never had a disposition gate of its own
  // (`canEditOutput` keys on selection + assignment + not-human-audit, nothing about the
  // call's lifecycle), so keeping one here would have stopped editing at exactly the point
  // the write-up begins. Edits after the disposition still reach the case — the call→case
  // sync fires on call update — and human audit recomputes the disposition from the final
  // values.
  //
  // Deliberately NOT gated on `callScriptAvailable` either. That requires
  // isHumanCallState(case.state), which goes false the moment the call is marked completed
  // and the case advances to post_call_review / human audit — i.e. precisely when the rep
  // sits down to write the call up. The COLUMN is already not gated on it for that exact
  // reason; gating editing on it put the lifecycle check back in through the side door and
  // left the script visible but frozen, with the "on recording" chips unclickable.
  const humanCallEditable = useMemo(
    () =>
      !!selectedCallDetails?.id &&
      selectedCallDetails.type === CallType.CALL_TYPE_HUMAN &&
      isAssignee,
    [selectedCallDetails?.id, selectedCallDetails?.type, isAssignee],
  );

  // Can MARK HEARD: only while the selected call is actually live. A strict subset of
  // editable — post-hang-up the rep fills values in rather than marking them heard.
  const humanCallActive = useMemo(
    () => humanCallEditable && selectedCallDetails?.status === CallStatus.CALL_STATUS_IN_PROGRESS,
    [humanCallEditable, selectedCallDetails?.status],
  );

  // Clear a call's "heard" marks (keys + timestamps, no PHI) once its disposition is
  // saved — the edit window has closed. Guard on the same id so switching the selected
  // call never clears the newly-selected call's marks.
  const prevDispoRef = useRef<{ id?: string; disposed: boolean }>({
    id: selectedCallDetails?.id,
    disposed: hasSavedCallDisposition,
  });
  useEffect(() => {
    const prev = prevDispoRef.current;
    prevDispoRef.current = { id: selectedCallDetails?.id, disposed: hasSavedCallDisposition };
    if (
      prev.id &&
      prev.id === selectedCallDetails?.id &&
      !prev.disposed &&
      hasSavedCallDisposition
    ) {
      clearHeard(prev.id);
    }
  }, [hasSavedCallDisposition, selectedCallDetails?.id]);

  // The ASSIGNEE's view follows the LIVE CALL. The script column lives in Review, so both
  // ends of a call land there: on dial, select the new call and switch to Review (the
  // column is gated on the latest human call being the SELECTED one, so selecting it is
  // what puts the script on screen); on hang-up, move to the recording with a toast. Other
  // viewers are never yanked between tabs or calls by someone else's call.
  const lastHumanCallIdRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (callScriptAvailable && callDetails?.id) lastHumanCallIdRef.current = callDetails.id;
  }, [callScriptAvailable, callDetails?.id]);

  const hadLiveCall = useRef(false);
  useEffect(() => {
    const liveCallId = callDetails?.id;
    // Track the transition for everyone, but only act on it for the assignee.
    const wasLiveCall = hadLiveCall.current;
    hadLiveCall.current = Boolean(liveCallId);
    if (!isAssignee) return;
    if (liveCallId && !wasLiveCall && callScriptAvailable) {
      setSelectedCallId(liveCallId);
      setManuallySelectedCallId(liveCallId);
      setActiveTab(CaseDetailsTabs.REVIEW);
    } else if (!liveCallId && wasLiveCall) {
      moveToCallRecording({
        lastHumanCallId: lastHumanCallIdRef.current,
        latestCallId,
        setActiveTab,
        setSelectedCallId,
        setManuallySelectedCallId,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callDetails?.id, callScriptAvailable, isAssignee]);

  return { humanCallEditable, humanCallActive };
}
